<script lang="ts">
	import { onMount } from 'svelte';

	import { LocalTaskStore } from '$lib/persist/local';
	import type { TaskStore } from '$lib/persist/port';
	import { createTaskStore, type TaskDraft } from '$lib/store/tasks';
	import { createTicker } from '$lib/store/ticker';
	import { pearlBalance, canAfford } from '$lib/store/pearls';
	import { buildScene } from '$lib/scene/build';
	import { palette } from '$lib/render/palette';
	import { drawTank, drawForeground, drawFeed, bedTopAt, chestBounds } from '$lib/render/water';
	import { drawCreatures, place, drawHintBurst } from '$lib/render/creatures';
	import { pick } from '$lib/render/pick';
	import type { Frame } from '$lib/render/loop';
	import type { Task } from '$lib/types';
	import type { Creature, Scene, SyncMood } from '$lib/scene/types';
	import { createAuth, isSyncConfigured, type Account } from '$lib/auth/session';
	import { SyncingTaskStore, type SyncStatus } from '$lib/persist/sync/syncing';
	import { SupabaseRemote } from '$lib/persist/sync/remote';

	import Tank from '$lib/ui/Tank.svelte';
	import DateHeader, { today, formatDay } from '$lib/ui/DateHeader.svelte';
	import TaskSheet from '$lib/ui/TaskSheet.svelte';
	import CreatureSheet, { tapAction } from '$lib/ui/CreatureSheet.svelte';
	import ListView from '$lib/ui/ListView.svelte';
	import Controls from '$lib/ui/Controls.svelte';
	import Banner from '$lib/ui/Banner.svelte';
	import Settings from '$lib/ui/Settings.svelte';
	import Legend from '$lib/ui/Legend.svelte';
	import SyncPanel from '$lib/ui/SyncPanel.svelte';
	import { loadShortcut, matches as matchesShortcut } from '$lib/ui/shortcut';
	import { shouldAutoOpen, shouldShowRevealHint } from '$lib/store/settings';
	import { resolveTap, type TapOrigin } from '$lib/ui/tap';
	import { labelable, clampLabelX, declutterLabels } from '$lib/ui/reveal';
	import Calendar from '$lib/ui/Calendar.svelte';
	import { taskDatesOf, koiDatesOf } from '$lib/ui/calendar';

	const auth = createAuth();

	let account = $state<Account | null>(null);
	let syncStatus = $state<SyncStatus>({ state: 'idle' });

	const local = new LocalTaskStore();

	/**
	 * The store is built once, at module scope, and cannot be rebuilt when someone
	 * signs in half an hour later — so what it holds is a port that forwards to
	 * whichever store is current. Signed out that is `local`, and every call is the
	 * one the app has always made.
	 *
	 * Handing `createTaskStore(local)` directly would look right and quietly break
	 * sync: writes would reach localStorage and never reach the debounced push.
	 */
	let active: TaskStore = local;
	let syncing: SyncingTaskStore | undefined;

	const port: TaskStore = {
		load: () => active.load(),
		save: (snapshot) => active.save(snapshot)
	};

	const store = createTaskStore(port);
	const { tasks, koi, settings, saveFailed } = store;

	/** Points `active` at a syncing store for this account, or back at plain local. */
	function useAccount(id: string | undefined) {
		// The status line is a description of whatever store `active` currently
		// points at. Swapping the store without this leaves the previous account's
		// state — including its `at` — on screen under the new account's email, or
		// under no account at all after a sign-out. `at` is deliberately never
		// persisted so one device cannot show another's sync time; carrying it
		// across an account swap on the same device is the same lie in miniature.
		syncStatus = { state: 'idle' };

		if (!id || !auth.client) {
			syncing = undefined;
			active = local;
			return;
		}

		syncing = new SyncingTaskStore({
			local,
			owner: id,
			remote: new SupabaseRemote(auth.client, id),
			onExternalChange: () => void store.hydrate(),
			onStatus: (status) => (syncStatus = status)
		});
		active = syncing;
	}

	let date = $state(today());

	/**
	 * Today, kept current while the app is open.
	 *
	 * Polled rather than derived: nothing else changes at midnight, so there is no
	 * reactive source to hang this off. Cheap — a string comparison a few times a
	 * minute — and it also catches a laptop waking on a different day, which is the
	 * case a single timer set at load would miss.
	 *
	 * The viewed date is deliberately *not* moved when this rolls over. Yanking someone
	 * to a new day mid-sentence is worse than letting the header say "Yesterday" and
	 * offer the way back.
	 */
	let now = $state(today());

	// Only so "3 minutes ago" ages while the sheet is open. The existing 20-second
	// rollover interval is a fine cadence for a line measured in minutes.
	let clock = $state(Date.now());

	let selected = $state<Task | null>(null);
	let editing = $state<Task | undefined>(undefined);
	let sheetOpen = $state(false);
	let listOpen = $state(false);
	let settingsOpen = $state(false);
	let legendOpen = $state(false);
	let calendarOpen = $state(false);

	// Tracks which sync error state the user has dismissed. Reset whenever the
	// state changes so a new error (or the same one after a sign-in attempt)
	// re-shows the banner.
	let dismissedSyncBanner = $state<string | null>(null);
	$effect(() => {
		// Track the state; if it changes to a new value, clear any previous dismissal.
		const s = syncStatus.state;
		if (s !== dismissedSyncBanner) dismissedSyncBanner = null;
	});

	/**
	 * The last frame the tank painted. Picking must answer against exactly what was
	 * drawn, so it reuses this rather than the wall clock.
	 */
	let lastFrame = { time: 0, size: { w: 0, h: 0 }, animate: true };

	/**
	 * Reveal-all: a tap on open water (no fish under the finger) surfaces every
	 * task's title at once instead of doing nothing, for a few seconds.
	 *
	 * `labelEls` is a plain object, not `$state` — `draw()` writes straight to each
	 * element's `style.transform` every frame so labels track their fish exactly
	 * the way the canvas does, without turning the render loop into something that
	 * triggers a Svelte re-render sixty times a second.
	 */
	type RevealEntry = { creature: Creature; x: number; y: number };
	let revealEntries = $state<RevealEntry[]>([]);
	let revealTimer: ReturnType<typeof setTimeout> | undefined;
	let labelEls: Record<string, HTMLElement | undefined> = {};
	const REVEAL_MS = 3200;

	/**
	 * The hint fish's pop, drawn on the canvas rather than as a `$state` value — same
	 * reasoning as `labelEls`: `draw()` reads this every frame to paint the burst,
	 * and a plain variable keeps that out of Svelte's reactivity entirely. Cleared
	 * once `drawHintBurst` reports the burst has run its course.
	 */
	let hintBurst: { x: number; y: number; at: number } | null = null;
	const HINT_BURST_MS = 550;

	/**
	 * Where the hint sign plants itself, in real canvas pixels. `$state`, unlike
	 * `hintBurst`, because it drives a CSS position rather than a per-frame canvas
	 * paint — but `updateHintSignPosition` only ever assigns a new object when the
	 * numbers actually changed, so this still only writes on an actual resize, not
	 * every frame.
	 *
	 * Deliberately reads `bedTopAt` rather than guessing a fixed offset from the
	 * bottom of the screen: the sand's height is a fraction of the tank's own
	 * height (`bedTopAt`'s `size.h * 0.12`), so a fixed CSS `bottom` lines up with
	 * the sand only by coincidence, on whatever one viewport it was tuned against,
	 * and drifts on every other one. `bedTopAt` is the one place that answers
	 * "where is the sand" — the chest and the planting already read it rather than
	 * each drawing their own idea of the line, and this is the same rule.
	 */
	let hintSignAt: { x: number; bottom: number } | null = $state(null);
	const HINT_SIGN_X_FRACTION = 0.78;

	function updateHintSignPosition(size: { w: number; h: number }) {
		if (!size.w || !size.h) return;

		let x = size.w * HINT_SIGN_X_FRACTION;
		// Clear of the chest on an unusual aspect ratio, the same way the pearls
		// sidestep it rather than drawing on top of the lid.
		const chest = chestBounds(size);
		if (x > chest.from - 40 && x < chest.to + 40) {
			x = chest.to + 60 <= size.w - 20 ? chest.to + 60 : Math.max(20, chest.from - 100);
		}

		const bottom = size.h - bedTopAt(x, size);
		if (hintSignAt && hintSignAt.x === x && hintSignAt.bottom === bottom) return;
		hintSignAt = { x, bottom };
	}

	// Prototype: the corner fish. Not wired into anything but its own tint.
	let browserOnline = $state(true);
	const syncMood = $derived<SyncMood>(
		!isSyncConfigured() || !account ? 'signed-out' : !browserOnline || syncStatus.state === 'offline' ? 'offline' : 'online'
	);

	const pearls = $derived(pearlBalance($tasks));
	// One-way latch: once popped, `showHint` never goes true again for this account.
	const showHint = $derived(shouldShowRevealHint($settings));
	const scene = $derived(buildScene($tasks, $koi, date, Date.now(), syncMood, showHint));
	const clearedPct = $derived(scene.clearedPct);
	// For the calendar's day markers — which dates have a live task, which earned a koi.
	const taskDates = $derived(taskDatesOf($tasks));
	const koiDates = $derived(koiDatesOf($koi));

	// Only after hydrating: before the store loads, every day looks empty, and a
	// message that flashes on every launch is worse than none.
	let hydrated = $state(false);
	// Neither the sync fish nor the hint fish is task data — both swim on an empty
	// day too, so neither may count toward whether the day looks empty.
	const emptyDay = $derived(
		hydrated && scene.creatures.every((c) => c.kind === 'sync' || c.kind === 'hint')
	);

	onMount(() => {
		// The static SEO hero in app.html exists only for crawlers and no-JS visitors,
		// before this component takes over — its <h1> duplicates DateHeader's once the
		// real app is up, so it comes out the moment there's a live app to replace it.
		document.querySelector('.seo-hero')?.remove();

		browserOnline = navigator.onLine;
		const goOnline = () => (browserOnline = true);
		const goOffline = () => (browserOnline = false);
		window.addEventListener('online', goOnline);
		window.addEventListener('offline', goOffline);

		const ticker = createTicker(store);
		store.hydrate().then(() => {
			hydrated = true;
			ticker.start();

			// Written the moment it is shown, not when it is closed: a reload mid-view
			// must not bring it back. The write goes through `commit` like any other
			// mutation, so a storage failure surfaces on the existing banner.
			if (shouldAutoOpen(store.snapshot().settings)) {
				legendOpen = true;
				store.markLegendSeen();
			}
		});

		const rollover = () => {
			now = today();
			clock = Date.now();
		};
		const timer = setInterval(rollover, 20_000);

		// Which account the store is currently holding data for. `onAuthStateChange` also
		// fires on a token refresh, roughly hourly, with the same identity — rebuilding
		// the sync stack for that would throw away an in-flight debounce and cost a full
		// round trip for nothing.
		let heldAccount: string | undefined;

		const unsubscribe = auth.account.subscribe((next) => {
			account = next;
			if (next?.id === heldAccount) return;
			heldAccount = next?.id;

			// A sign-in on a device with unclaimed local data merges rather than asks:
			// the local snapshot is pushed and the remote pulled through the same rules.
			// A sign-in as a *different* account discards that data instead.
			useAccount(next?.id);

			// Re-hydrate before syncing, not as a consequence of it. `load()` applies the
			// account claim, so this is what drops the previous account's tank out of
			// memory — and it has to happen even when the first sync fails, or an edit
			// made while offline would be written back under the new account's name.
			void store.hydrate().then(() => syncing?.sync('account'));
		});

		// A sleeping machine runs no timers, so the wake is where the day usually turns.
		// A phone gives back more than one kind of wake: a frozen tab thaws with
		// `resume` and a back/forward-cache restore only fires `pageshow`. Each event
		// on the one target that fires it. `visibilitychange` is fired at the document
		// and bubbles, so listening on both meant every tab switch woke the tank twice;
		// `pageshow` and `focus` are window events and never reached the document
		// listener at all.
		const wake = () => {
			rollover();
			void syncing?.sync('wake');
		};
		const wakeTargets: [EventTarget, string][] = [
			[document, 'visibilitychange'],
			[document, 'resume'],
			[window, 'pageshow'],
			[window, 'focus']
		];
		for (const [target, event] of wakeTargets) target.addEventListener(event, wake);

		// Both the interval and the wake listeners leak without this.
		return () => {
			ticker.stop();
			clearInterval(timer);
			unsubscribe();
			for (const [target, event] of wakeTargets) target.removeEventListener(event, wake);
			window.removeEventListener('online', goOnline);
			window.removeEventListener('offline', goOffline);
		};
	});

	/**
	 * Paints one frame.
	 *
	 * Reads the store with `snapshot()` — a `get()` underneath — rather than
	 * subscribing. That is the property Svelte was chosen for: the loop runs sixty
	 * times a second and must never trigger a component re-render.
	 */
	function draw(ctx: CanvasRenderingContext2D, frame: Frame, size: { w: number; h: number }) {
		const state = store.snapshot();
		const scene = buildScene(
			state.tasks,
			state.koi,
			date,
			Date.now(),
			syncMood,
			shouldShowRevealHint(state.settings)
		);
		const colors = palette(state.settings.environment, scene.clearedPct);
		updateHintSignPosition(size);

		// Under reduced motion the clock is pinned, so ambient drift holds still
		// while state changes still repaint.
		const time = frame.animate ? frame.time : 0;
		lastFrame = { time, size, animate: frame.animate };

		drawTank(ctx, size, colors, time, scene.pearls);
		// Behind the creatures: the fish rise through the food, not under it.
		drawFeed(ctx, size, time, frame.animate ? scene.feeding : 0);
		drawCreatures(ctx, scene.creatures, colors, size, time, frame.animate, scene.feeding);
		// Haze and vignette last, so they sit over the creatures and give the tank depth.
		drawForeground(ctx, size, colors);

		// After the haze, not before: `lighter`-composited light has to be added on
		// top of the wash to survive it, same reason the chest's gold and gem
		// twinkles draw here rather than with the creatures.
		if (hintBurst) {
			const progress = (Date.now() - hintBurst.at) / HINT_BURST_MS;
			drawHintBurst(ctx, hintBurst.x, hintBurst.y, progress);
			if (progress >= 1) hintBurst = null;
		}

		// Direct DOM writes, not a `$state` assignment — see the comment on
		// `revealEntries` for why this must not become a Svelte re-render.
		//
		// Declutter every frame, not just on the initial tap: `place()` keeps
		// moving each fish, so positions decluttered once would drift back on top
		// of each other as soon as the fish swam. Guarded on length so the two
		// allocations below only happen while a reveal is actually on screen,
		// not on every frame for the rest of the session.
		if (revealEntries.length) {
			const points = revealEntries.map((entry) => {
				const at = place(entry.creature, size, time, frame.animate);
				return { id: entry.creature.id, x: clampLabelX(at.x, size.w), y: at.y };
			});
			for (const point of declutterLabels(points)) {
				const el = labelEls[point.id];
				if (!el) continue;
				el.style.transform = `translate(${point.x}px, ${point.y}px)`;
			}
		}
	}

	// Tap detection for mobile.
	//
	// Problems solved:
	// 1. Android Chrome fires pointercancel instead of pointerup when it decides a
	//    gesture is a scroll — even with touch-action:none on a child canvas.
	//    setPointerCapture forces all subsequent pointer events (including pointerup)
	//    to the element that captured, preventing the cancel.
	// 2. Android's touch slop is ~16 device px (~8 CSS px at 2x DPR). Taps that
	//    drift within that range must still register. TAP_SLOP is set to 20px to
	//    give comfortable margin on all Android densities.
	// 3. pointerId tracks the specific touch so multi-touch does not corrupt state.
	let tapOrigin: TapOrigin | null = null;
	const TAP_SLOP = 20;

	let lastPointerWasTouch = false;

	function tankPointerDown(event: PointerEvent) {
		// Capture so pointerup/pointercancel always come back to this element.
		(event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);

		// Point and time are captured now, at the instant the user aims, not later at
		// pointerup — a fish swims on for the length of the gesture, so hit-testing
		// against pointerup's position turns a tap on a moving fish into a near-miss.
		const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
		tapOrigin = {
			id: event.pointerId,
			clientX: event.clientX,
			clientY: event.clientY,
			point: { x: event.clientX - rect.left, y: event.clientY - rect.top },
			time: lastFrame.time
		};
		lastPointerWasTouch = event.pointerType === 'touch';
	}

	function tankPointerCancel(event: PointerEvent) {
		if (tapOrigin?.id === event.pointerId) tapOrigin = null;
	}

	/** Surfaces every labelable creature's title — shared by an open-water tap and a first tap on the hint fish. */
	function revealAll(scene: Scene, time: number) {
		clearTimeout(revealTimer);
		const creatures = labelable(scene.creatures);
		const raw = creatures.map((creature) => {
			const at = place(creature, lastFrame.size, time, lastFrame.animate);
			return { id: creature.id, x: clampLabelX(at.x, lastFrame.size.w), y: at.y };
		});
		const declut = new Map(declutterLabels(raw).map((point) => [point.id, point]));
		revealEntries = creatures.map((creature) => {
			const point = declut.get(creature.id)!;
			return { creature, x: point.x, y: point.y };
		});
		revealTimer = setTimeout(() => {
			revealEntries = [];
		}, REVEAL_MS);
	}

	function tapTank(event: PointerEvent) {
		const resolved = resolveTap(
			tapOrigin,
			{ id: event.pointerId, clientX: event.clientX, clientY: event.clientY },
			TAP_SLOP
		);
		tapOrigin = null;
		if (!resolved) return; // scroll, not a tap — or a different pointer

		const state = store.snapshot();
		const scene = buildScene(
			state.tasks,
			state.koi,
			date,
			Date.now(),
			syncMood,
			shouldShowRevealHint(state.settings)
		);
		const hit = pick(scene.creatures, resolved.point, lastFrame.size, resolved.time, lastFrame.animate);

		// A tap that lands on open water, not on any fish, reveals every task's title
		// at once instead of doing nothing — the tank stays glanceable without having
		// to tap each fish in turn to find one.
		if (!hit) {
			revealAll(scene, resolved.time);
			return;
		}

		// The overflow treat stands for several tasks at once, so it cannot open a
		// sheet — it opens the list, which is the view that can show them all.
		if (hit && !hit.taskId && hit.kind === 'treat') {
			listOpen = true;
			return;
		}

		// The sync fish stands for connection state, not a task — tapping opens the
		// same sync status the settings sheet already shows, rather than a new sheet.
		if (hit && hit.kind === 'sync') {
			settingsOpen = true;
			return;
		}

		// The hint fish stands for no task either. Precision varies — a tap aimed at
		// it can land squarely on its body rather than the water beside it — so a
		// direct hit before its own label has shown teaches nothing: it would pop the
		// one thing this whole mechanism exists to teach without ever revealing what
		// tapping water does. Only a second tap, once the label is already up, pops
		// it; the first behaves exactly like tapping open water.
		if (hit && hit.kind === 'hint') {
			const alreadyRevealed = revealEntries.some((entry) => entry.creature.kind === 'hint');
			if (alreadyRevealed) {
				hintBurst = { x: resolved.point.x, y: resolved.point.y, at: Date.now() };
				void store.markRevealHintSeen();
			} else {
				revealAll(scene, resolved.time);
			}
			return;
		}

		// Pearls stand for no task at all.
		const task = hit?.taskId ? state.tasks.find((t) => t.id === hit.taskId) : undefined;
		if (!task) return;

		switch (tapAction(task, canAfford(state.tasks, task))) {
			case 'release':
				store.releaseBubble(task.id);
				break;
			case 'claim':
				store.claimTreat(task.id);
				break;
			case 'sheet':
				selected = task;
				break;
		}
	}

	function openAdd() {
		editing = undefined;
		sheetOpen = true;
	}

	function openEdit(task: Task) {
		editing = task;
		selected = null;
		sheetOpen = true;
	}

	function save(draft: TaskDraft) {
		return editing
			? store.editTask(editing.id, draft)
			: store.addTask(draft);
	}

	/**
	 * The canvas offers a keyboard user nothing — the corner "Task list" button is
	 * the only Tab-reachable route in, and nothing on screen says so. A shortcut set
	 * in Settings opens the same list view as that button, for anyone who reaches
	 * for one instead of hunting the corners.
	 *
	 * Blank by default rather than shipping a bound key: a bare, unmodified letter
	 * is exactly what Vim-style browser extensions (Vimium and friends) bind, and
	 * they intercept the keydown before the page ever receives it — confirmed live,
	 * an earlier default of Shift+L did nothing until isolated to an incognito
	 * window with extensions off. Read fresh from `loadShortcut()` on every keydown
	 * rather than cached, so a shortcut set in Settings takes effect immediately.
	 *
	 * Also guarded on: no sheet already open (firing under another sheet would be a
	 * surprise, not a convenience), and the event isn't destined for a text field.
	 */
	function handleGlobalKey(e: KeyboardEvent) {
		if (!matchesShortcut(e, loadShortcut())) return;
		const target = e.target as HTMLElement | null;
		if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
		if (target?.isContentEditable) return;
		if (sheetOpen || listOpen || settingsOpen || legendOpen || selected) return;

		e.preventDefault();
		listOpen = true;
	}


</script>

<svelte:head>
	<title>Fish Tank</title>
</svelte:head>

<svelte:window onkeydown={handleGlobalKey} />

<main>
	<!-- Pointer picking lives on the wrapper. A canvas offers nothing to a keyboard or
	     a screen reader, so no ARIA role here would be honest: the List view is the
	     genuine keyboard-reachable route to every one of these actions, backed by the
	     same store. That is why it is a first-class second view rather than a fallback. -->
	<!-- svelte-ignore a11y_no_static_element_interactions -->
	<div
		class="tank"
		onpointerdown={tankPointerDown}
		onpointerup={tapTank}
		onpointercancel={tankPointerCancel}
		oncontextmenu={(e) => { if (lastPointerWasTouch) e.preventDefault(); }}
	>
		<p class="visually-hidden">
			This tank is a decorative canvas, not usable with a keyboard or screen reader.
			Use the "Task list" button (top-left corner) for a fully accessible view of
			the same tasks. A keyboard shortcut for it can be set in Settings.
		</p>
		<Tank {draw} />

		{#if revealEntries.length}
			<div class="reveal-layer" aria-hidden="true">
				{#each revealEntries as entry (entry.creature.id)}
					<span
						class="reveal-label"
						style="transform: translate({entry.x}px, {entry.y}px)"
						bind:this={labelEls[entry.creature.id]}
					>
						<span class="reveal-label__pill">{entry.creature.label}</span>
					</span>
				{/each}
			</div>
		{/if}

		<!-- Tied to the hint fish, not a separate flag: gone the moment it pops,
		     same as the fish itself. A standing reminder for anyone who never taps
		     the fish's own label into view. -->
		{#if showHint && hintSignAt}
			<div
				class="hint-sign"
				aria-hidden="true"
				style="left: {hintSignAt.x}px; bottom: {hintSignAt.bottom}px;"
			>
				<span class="hint-sign__board">Tap the water</span>
				<span class="hint-sign__post"></span>
				<span class="hint-sign__shadow"></span>
				<svg class="hint-sign__weeds" viewBox="0 0 64 26" aria-hidden="true">
					<path d="M8 26 C6 16 12 10 10 2" fill="none" stroke="#2f6b2c" stroke-width="3" stroke-linecap="round" />
					<path d="M18 26 C19 14 14 9 17 0" fill="none" stroke="#3f7d3a" stroke-width="3" stroke-linecap="round" />
					<path d="M32 26 C31 12 36 8 33 1" fill="none" stroke="#5a9c4f" stroke-width="3" stroke-linecap="round" />
					<path d="M46 26 C48 15 42 10 45 2" fill="none" stroke="#3f7d3a" stroke-width="3" stroke-linecap="round" />
					<path d="M56 26 C54 17 59 11 57 3" fill="none" stroke="#2f6b2c" stroke-width="3" stroke-linecap="round" />
				</svg>
			</div>
		{/if}
	</div>

	{#snippet syncSection()}
		<SyncPanel
			{account}
			status={syncStatus}
			now={clock}
			onSignIn={() => void auth.signIn()}
			onSignOut={() => void auth.signOut()}
			onSyncNow={() => void syncing?.sync('manual')}
		/>
	{/snippet}

	<div class="chrome">
		<DateHeader
			{date}
			environment={$settings.environment}
			{clearedPct}
			{now}
			onNavigate={(next) => (date = next)}
			onOpenCalendar={() => (calendarOpen = true)}
		/>
	</div>

	{#if emptyDay}
		<p class="empty">
			<span>Nothing in the tank for {formatDay(date)}.</span>
			<small>Add a task and it will start swimming.</small>
		</p>
	{/if}

	<!--
	  Local storage failing is strictly worse than the cloud failing, so it wins the one
	  banner. Only sync failures that will never fix themselves get one at all: a phone
	  loses signal constantly, and nagging about it teaches you to ignore the banner —
	  which costs you the three below.
	-->
	{#if $saveFailed}
		<Banner visible={true} />
	{:else if syncStatus.state === 'denied' && dismissedSyncBanner !== 'denied'}
		<Banner
			visible={true}
			title="Sync is signed out."
			detail="Your tasks are safe on this device. Open Settings to sign in again."
			onDismiss={() => (dismissedSyncBanner = 'denied')}
		/>
	{:else if syncStatus.state === 'stale' && dismissedSyncBanner !== 'stale'}
		<Banner
			visible={true}
			title="This device is out of date."
			detail="It will not sync until the app is reloaded to pick up the newer version."
			onDismiss={() => (dismissedSyncBanner = 'stale')}
		/>
	{:else if syncStatus.state === 'rejected' && dismissedSyncBanner !== 'rejected'}
		<Banner
			visible={true}
			title="The server refused this data."
			detail="Your tasks are safe on this device, but they are not reaching your other ones."
			onDismiss={() => (dismissedSyncBanner = 'rejected')}
		/>
	{/if}

	<Controls
		{pearls}
		onAdd={openAdd}
		onOpenList={() => (listOpen = true)}
		onOpenSettings={() => (settingsOpen = true)}
	/>

	<CreatureSheet
		task={selected}
		affordable={selected ? canAfford($tasks, selected) : false}
		balance={pearls}
		onComplete={(id) => store.completeTask(id)}
		onReopen={(id) => store.reopenTask(id)}
		onRelease={(id) => store.releaseBubble(id)}
		onClaim={(id) => store.claimTreat(id)}
		onEdit={openEdit}
		onMove={(id, to) => store.moveToDate(id, to)}
		onDelete={(id) => store.softDelete(id)}
		onTogglePriority={(id) => store.togglePriority(id)}
		onClose={() => (selected = null)}
	/>

	<TaskSheet
		open={sheetOpen}
		{date}
		tasks={$tasks}
		task={editing}
		onSave={save}
		onClose={() => (sheetOpen = false)}
	/>

	<ListView
		open={listOpen}
		{date}
		tasks={$tasks}
		onComplete={(id) => store.completeTask(id)}
		onReopen={(id) => store.reopenTask(id)}
		onRelease={(id) => store.releaseBubble(id)}
		onClaim={(id) => store.claimTreat(id)}
		onEdit={openEdit}
		onMove={(id, to) => store.moveToDate(id, to)}
		onDelete={(id) => store.softDelete(id)}
		onClose={() => (listOpen = false)}
	/>

	<Settings
		open={settingsOpen}
		environment={$settings.environment}
		onChange={(environment) => store.setEnvironment(environment)}
		onOpenLegend={() => (legendOpen = true)}
		onClose={() => (settingsOpen = false)}
		sync={isSyncConfigured() ? syncSection : undefined}
	/>

	<Legend
		open={legendOpen}
		environment={$settings.environment}
		onClose={() => (legendOpen = false)}
	/>

	<Calendar
		open={calendarOpen}
		{date}
		{now}
		{taskDates}
		{koiDates}
		onNavigate={(next) => (date = next)}
		onClose={() => (calendarOpen = false)}
	/>


</main>

<style>
	:global(body) {
		margin: 0;
		background: #4fc3d9;
		font-family:
			system-ui,
			-apple-system,
			'Segoe UI',
			sans-serif;
		overscroll-behavior: none;
	}

	main {
		position: relative;
		height: 100dvh;
		overflow: hidden;
	}

	.tank {
		position: absolute;
		inset: 0;
		/* Must match canvas touch-action so the browser does not cancel the touch
		   sequence on the child before setPointerCapture fires on this element. */
		touch-action: none;
	}

	.reveal-layer {
		position: absolute;
		inset: 0;
		overflow: hidden;
		/* Decorative, like the canvas beneath it — never in the way of a tap. */
		pointer-events: none;
	}

	.reveal-label {
		position: absolute;
		left: 0;
		top: 0;
		will-change: transform;
	}

	.reveal-label__pill {
		display: inline-block;
		transform: translate(-50%, -170%);
		/* Wide enough to show more of a long title on a phone-width viewport, capped
		   so it can never itself force the pill past the glass on either side —
		   `clampLabelX`'s own margin assumes a pill no wider than this. */
		max-width: min(168px, 44vw);
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
		padding: 3px 7px;
		border-radius: 8px;
		background: rgba(10, 30, 40, 0.72);
		color: #fff;
		font-size: 11px;
		font-weight: 600;
		line-height: 1.3;
	}

	.hint-sign {
		position: absolute;
		/* `width: max-content` matters: with only `left` set, an absolutely
		   positioned box's shrink-to-fit width is computed against the space
		   remaining to the right of `left`, not the container's full width — at
		   a large `left` that space can be too narrow to hold "Tap the water" on
		   one line, wrapping it before the centering transform below ever runs. */
		width: max-content;
		/* `left` and `bottom` are set inline, from `hintSignAt` — computed against
		   `bedTopAt`, the tank's own answer for where the sand sits, rather than a
		   fixed guess here that only happens to line up on one viewport size. */
		transform: translateX(-50%);
		display: flex;
		flex-direction: column;
		align-items: center;
		pointer-events: none;
	}

	.hint-sign__board {
		position: relative;
		background:
			repeating-linear-gradient(
				100deg,
				rgba(0, 0, 0, 0.08) 0 2px,
				transparent 2px 9px
			),
			linear-gradient(180deg, #cf9152, #a2652f);
		border: 3px solid #6e4620;
		border-radius: 0.5rem;
		padding: 0.6rem 1.3rem;
		color: #fff3d0;
		font-size: 1.05rem;
		font-weight: 700;
		letter-spacing: 0.01em;
		text-shadow:
			0 1px 0 rgba(0, 0, 0, 0.35),
			0 -1px 0 rgba(255, 255, 255, 0.12);
		box-shadow: 0 4px 14px rgba(0, 0, 0, 0.3);
	}

	/* A wisp of moss on the corner — the sign has clearly sat here a while. */
	.hint-sign__board::before {
		content: '';
		position: absolute;
		top: -0.3rem;
		left: -0.4rem;
		width: 1rem;
		height: 0.7rem;
		background: radial-gradient(ellipse at 30% 30%, #6fae4f, #3d6f2c 70%);
		border-radius: 60% 40% 55% 45% / 60% 50% 50% 40%;
		box-shadow: 0.5rem 0.15rem 0 -0.1rem #59923f;
	}

	.hint-sign__post {
		width: 0.6rem;
		/* With the shadow now an overlay (see .hint-sign__shadow) rather than a
		   flex child, the post is the last flow child, so its own height is
		   what determines how far it reaches — no more fighting a fixed anchor
		   elsewhere in the stack. A few px past the measured contact point so
		   it reads as planted, not balanced exactly on the line. */
		height: 2.7rem;
		background: linear-gradient(90deg, #6d431e, #8a5a2a 45%, #6d431e);
		border-radius: 0 0 0.15rem 0.15rem;
	}

	/*
	 * Absolutely positioned, not a flex child — a normal-flow shadow below the
	 * post was, by construction, the box's actual last child, so it (not the
	 * post) was what sat on the container's `bottom`-anchored edge. Growing the
	 * post taller pushed the whole sign upward instead of closing the gap, since
	 * the post's own bottom was never the anchored edge to begin with. As an
	 * overlay the post genuinely becomes the bottom-most element, its own
	 * bottom landing exactly on `bedTopAt`.
	 */
	.hint-sign__shadow {
		position: absolute;
		left: 50%;
		bottom: -0.15rem;
		width: 2.4rem;
		height: 0.5rem;
		transform: translateX(-50%);
		background: radial-gradient(ellipse, rgba(0, 0, 0, 0.4), transparent 72%);
		border-radius: 50%;
	}

	.hint-sign__weeds {
		position: absolute;
		left: 50%;
		bottom: -0.15rem;
		width: 3.2rem;
		height: 1.3rem;
		transform: translateX(-50%);
		overflow: visible;
	}

	.visually-hidden {
		position: absolute;
		width: 1px;
		height: 1px;
		overflow: hidden;
		clip-path: inset(50%);
		white-space: nowrap;
	}

	.chrome {
		position: relative;
		pointer-events: none;
	}

	.chrome :global(button) {
		pointer-events: auto;
	}

	.empty {
		position: absolute;
		top: 45%;
		left: 0;
		right: 0;
		display: grid;
		gap: 0.35rem;
		margin: 0;
		text-align: center;
		color: #fff;
		text-shadow: 0 1px 6px rgba(0, 0, 0, 0.3);
		pointer-events: none;
	}

	.empty span {
		font-size: 1rem;
	}

	.empty small {
		font-size: 0.85rem;
		opacity: 0.8;
	}


</style>
